import { Clock } from 'lucide-react'
import { cn } from '@/lib/cn'
import {
  formatDuration,
  slaToneBgClass,
  slaToneTextClass,
  useSlaTimer,
} from '../../hooks/useSlaTimer'
import { isTerminalStatus } from '../../types'
import type { AlertStatus, SlaInfo } from '../../types'

/**
 * AF-126 — full-width SLA banner for the detail page.
 *
 * Shows label + target + a thin progress bar that fills over time. When
 * the SLA is breached, the bar uses a striped pattern past 100% and pulses.
 */
export function SlaBanner({ sla, status }: { sla: SlaInfo; status: AlertStatus }) {
  const active = !isTerminalStatus(status)
  const { label, tone, progress, breached } = useSlaTimer({
    startedAt: sla.started_at,
    deadlineAt: sla.deadline_at,
    active,
  })

  const targetMinutes = sla.target_minutes
  const targetLabel = formatDuration(targetMinutes * 60_000)

  return (
    <div className="flex flex-col gap-1 rounded-md border border-border bg-card px-4 py-2">
      <div className="flex items-center gap-2 text-xs">
        <Clock size={12} className={cn(slaToneTextClass(tone))} />
        <span className={cn('font-medium', slaToneTextClass(tone))}>{label}</span>
        <span className="text-muted-fg">of {targetLabel} SLA</span>
        {!active && <span className="ml-2 text-muted-fg">(closed)</span>}
      </div>
      <div className="relative h-1 overflow-hidden rounded-full bg-muted">
        {breached ? (
          <div
            className={cn(
              'absolute inset-y-0 left-0 right-0 animate-pulse',
              'bg-[repeating-linear-gradient(45deg,var(--color-status-failed)_0,var(--color-status-failed)_4px,transparent_4px,transparent_8px)]',
            )}
            aria-hidden
          />
        ) : (
          <div
            className={cn('absolute inset-y-0 left-0 transition-[width]', slaToneBgClass(tone))}
            style={{ width: `${Math.min(100, progress)}%` }}
            aria-hidden
          />
        )}
      </div>
    </div>
  )
}
